import React, { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import csvImportApiClient from '../../utilities/csvImportApiClient';
import Styles from './styles.module.css';
import Loader from '../../assets/svg/loader.svg';
import { ReactComponent as UploadSvg } from '../../assets/svg/uploadIcon.svg';

const CSVImporter = ({ selectedCustomer, selectedBillingAddress, selectedShippingAddress, selectedProfile, salesChannels = [], shippingMethods = [] }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [isImporting, setIsImporting] = useState(false);
    const [importStats, setImportStats] = useState(null);

    // Validate CSV structure against profile
    const validateCSVStructure = async (file, profile) => {
        if (!profile || !profile.fieldMapping) {
            return true; // No profile to validate against
        }

        try {
            // Parse CSV headers and data
            const parseResult = await csvImportApiClient.parseCSVHeader(file);
            
            if (!parseResult.success) {
                alert('Fehler beim Lesen der CSV-Datei: ' + (parseResult.message || 'Unbekannter Fehler'));
                return false;
            }

            // Safe access to headers with fallback
            const csvHeaders = parseResult.data?.headers || [];
            if (csvHeaders.length === 0) {
                alert('Fehler: CSV-Datei hat keine erkennbaren Spaltenüberschriften.');
                return false;
            }
            const profileMapping = profile.fieldMapping;
            
            // Get only mapped columns from profile (ignore unmapped CSV columns)
            const mappedColumns = Object.keys(profileMapping).filter(col => profileMapping[col]); // Only columns with actual mapping
            
            // Check if mapped columns exist in CSV
            const missingInCSV = mappedColumns.filter(col => !csvHeaders.includes(col));
            
            console.log('[CSV Validation] Profile expects columns:', mappedColumns);
            console.log('[CSV Validation] CSV has columns:', csvHeaders);
            console.log('[CSV Validation] Missing in CSV:', missingInCSV);
            
            // Show delimiter info if available
            if (parseResult.delimiterInfo) {
                console.log('[CSV Validation] Delimiter info:', parseResult.delimiterInfo);
                
                // Show delimiter info as user feedback (only if not confident)
                if (parseResult.delimiterInfo.confidence < 90) {
                    console.warn('⚠️ CSV-Trennzeichen-Warnung: ' + parseResult.delimiterInfo.message);
                }
            }
            
            // Critical error: Profile expects columns that CSV doesn't have
            if (missingInCSV.length > 0) {
                const errorMessage = `❌ CSV-Struktur-Fehler!\n\n` +
                                  `Das Profil "${profile.name}" erwartet Spalten, die in der CSV-Datei fehlen:\n\n` +
                                  `❌ Fehlende Spalten: ${missingInCSV.join(', ')}\n\n` +
                                  `📄 CSV Spalten: ${csvHeaders.join(', ')}\n\n` +
                                  `🔧 Lösung: CSV-Datei anpassen oder Profil-Mapping korrigieren.`;
                alert(errorMessage);
                return false;
            }
            
            // Validate critical field mappings
            const criticalFieldValidation = validateCriticalFields(profile, csvHeaders);
            if (!criticalFieldValidation.isValid) {
                alert(criticalFieldValidation.message);
                return false;
            }
            
            // Show success message with delimiter info
            let successMessage = `✅ CSV-Struktur validiert! Alle ${mappedColumns.length} gemappten Spalten sind vorhanden.`;
            if (parseResult.delimiterInfo && parseResult.delimiterInfo.confidence >= 90) {
                successMessage += ` (${parseResult.delimiterInfo.message})`;
            }
            console.log(successMessage);
            
            // Validate product existence
            const productValidation = await validateProductExistence(file, profile);
            if (!productValidation) {
                return false;
            }
            
            // All good
            return true;
            
        } catch (error) {
            console.error('CSV validation error:', error);
            throw error;
        }
    };

    // Validate critical fields like OrderNumber and Date
    const validateCriticalFields = (profile, csvHeaders) => {
        const fieldMapping = profile.fieldMapping;
        const criticalFields = {
            orderNumber: ['orderNumber', 'order.orderNumber'],
            orderDate: ['orderDate', 'orderDateTime', 'order.orderDate', 'order.orderDateTime']
        };
        
        const missingCriticalFields = [];
        
        // Check if OrderNumber is mapped
        const hasOrderNumberMapping = Object.keys(fieldMapping).some(csvCol => {
            const shopwareField = fieldMapping[csvCol];
            return shopwareField && criticalFields.orderNumber.some(field => shopwareField.includes(field));
        });
        
        if (!hasOrderNumberMapping) {
            missingCriticalFields.push('Bestellnummer (OrderNumber)');
        }
        
        // Check if Date is mapped
        const hasDateMapping = Object.keys(fieldMapping).some(csvCol => {
            const shopwareField = fieldMapping[csvCol];
            return shopwareField && criticalFields.orderDate.some(field => shopwareField.includes(field));
        });
        
        if (!hasDateMapping) {
            missingCriticalFields.push('Bestelldatum (OrderDate)');
        }
        
        if (missingCriticalFields.length > 0) {
            return {
                isValid: false,
                message: `❌ Kritische Felder fehlen im Profil-Mapping!\n\n` +
                        `Die folgenden wichtigen Felder sind nicht zugeordnet:\n\n` +
                        `❌ ${missingCriticalFields.join(', ')}\n\n` +
                        `🔧 Bitte bearbeiten Sie das Profil und ordnen Sie diese Felder zu.`
            };
        }
        
        return { isValid: true };
    };

    // Validate that all products in CSV exist in the system
    const validateProductExistence = async (file, profile) => {
        try {
            // Find product number field in mapping
            const productNumberField = Object.keys(profile.fieldMapping).find(csvCol => {
                const shopwareField = profile.fieldMapping[csvCol];
                return shopwareField.includes('product.productNumber') || 
                       shopwareField.includes('lineItems.product.productNumber');
            });

            if (!productNumberField) {
                // No product field mapped, skip validation
                return true;
            }

            // Read full CSV file to extract all product numbers
            const csvData = await readFullCSVFile(file);
            const productNumbers = csvData
                .map(row => row[productNumberField])
                .filter(num => num && num.trim())
                .map(num => num.trim())
                .filter((num, index, arr) => arr.indexOf(num) === index); // Remove duplicates

            if (productNumbers.length === 0) {
                alert('❌ Keine Produktnummern in der CSV-Datei gefunden.');
                return false;
            }

            // Check product existence via API
            console.log('[Product Validation] Checking products:', productNumbers);
            const productCheckResult = await csvImportApiClient.findProductsByNumbers(productNumbers);
            
            if (!productCheckResult.success) {
                alert('❌ Fehler beim Prüfen der Produktexistenz: ' + productCheckResult.message);
                return false;
            }

            const foundProducts = productCheckResult.data.products || [];
            const foundProductNumbers = foundProducts.map(p => p.productNumber);
            const missingProducts = productNumbers.filter(num => !foundProductNumbers.includes(num));

            if (missingProducts.length > 0) {
                const errorMsg = `❌ Artikel-Validierung fehlgeschlagen!\n\n` +
                               `${missingProducts.length} von ${productNumbers.length} Artikeln existieren nicht im System:\n\n` +
                               `${missingProducts.slice(0, 10).join(', ')}` +
                               (missingProducts.length > 10 ? `\n\n...und ${missingProducts.length - 10} weitere` : '') +
                               `\n\nBitte korrigieren Sie die Produktnummern oder erstellen Sie die fehlenden Artikel im System.`;
                               
                alert(errorMsg);
                return false;
            }

            // All products found
            console.log('[Product Validation] All products found:', foundProducts.length);
            return true;

        } catch (error) {
            console.error('Product validation error:', error);
            alert('❌ Fehler beim Validieren der Artikel: ' + error.message);
            return false;
        }
    };

    // Helper function to read full CSV file - use the same logic as csvImportApiClient
    const readFullCSVFile = async (file) => {
        try {
            const csvData = await csvImportApiClient.readCSVFile(file);
            return csvData.data || [];
        } catch (error) {
            console.error('Error reading full CSV file:', error);
            throw error;
        }
    };

    const onDrop = async (acceptedFiles) => {
        if (acceptedFiles.length > 0) {
            const file = acceptedFiles[0];
            
            // Validate CSV structure against profile before setting
            if (selectedProfile) {
                try {
                    const isValid = await validateCSVStructure(file, selectedProfile);
                    if (!isValid) {
                        return; // Error already shown to user
                    }
                } catch (error) {
                    alert('Fehler beim Validieren der CSV-Struktur: ' + error.message);
                    return;
                }
            }
            
            setSelectedFile(file);
            setImportStats(null);
        }
    };

    // Re-validate CSV when profile changes
    useEffect(() => {
        if (selectedFile && selectedProfile) {
            validateCSVStructure(selectedFile, selectedProfile).then(isValid => {
                if (!isValid) {
                    // Remove invalid file when profile changes
                    setSelectedFile(null);
                    setImportStats(null);
                }
            }).catch(error => {
                console.error('Auto-validation error:', error);
                // Keep file but show warning in console
            });
        }
    }, [selectedProfile]); // validateCSVStructure is defined in this component and doesn't need to be in deps

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: '.csv',
        multiple: false
    });

    // Sales channels are now passed as props, no need to load them

    const handleImport = async () => {
        const salesChannelId = selectedProfile?.defaultValues?.salesChannelId;
        
        console.log('[CSV Import] Starting import with:', {
            file: selectedFile?.name,
            profile: selectedProfile?.name,
            salesChannel: salesChannelId,
            customer: selectedCustomer?.firstName + ' ' + selectedCustomer?.lastName
        });

        if (!selectedFile || !selectedProfile || !salesChannelId) {
            alert('Bitte wählen Sie eine Datei aus. Der Verkaufskanal wird aus dem Profil übernommen.');
            return;
        }

        // Re-validate CSV structure before import (in case profile changed after upload)
        try {
            const isValid = await validateCSVStructure(selectedFile, selectedProfile);
            if (!isValid) {
                return; // Error already shown to user
            }
        } catch (error) {
            alert('Fehler beim Validieren der CSV-Struktur: ' + error.message);
            return;
        }

        setIsImporting(true);
        setImportStats(null);

        try {
            console.log('[CSV Import] Calling importCSV API...');
            const response = await csvImportApiClient.importCSV(
                selectedFile,
                selectedCustomer,
                selectedBillingAddress,
                selectedShippingAddress,
                selectedProfile,
                salesChannelId
            );

            console.log('[CSV Import] API Response:', response);

            if (response.success) {
                const successMessage = response.data.ordersCreated > 0 
                    ? `${response.data.ordersCreated} Bestellung(en) erfolgreich erstellt.`
                    : 'Import erfolgreich abgeschlossen.';
                
                setImportStats({
                    imported: response.data.orderNumbers || [response.data.ordersCreated + ' Bestellung(en)'],
                    orders: response.data.orders || [],
                    total: response.data.ordersCreated || 1,
                    message: successMessage
                });
                
                // Log order details to console
                console.log('[CSV Import] Created Orders:', response.data.orders);
                console.log('[CSV Import] Order IDs:', response.data.orderIds);
                console.log('[CSV Import] Order Numbers:', response.data.orderNumbers);
                
                alert('Import erfolgreich abgeschlossen! ' + successMessage);
            } else {
                alert('Import fehlgeschlagen: ' + (response.message || 'Unbekannter Fehler'));
            }
        } catch (error) {
            console.error('Import error:', error);
            alert('Import fehlgeschlagen: ' + error.message);
        } finally {
            setIsImporting(false);
        }
    };

    const handleCancel = () => {
        setSelectedFile(null);
        setImportStats(null);
    };


    return (
        <div className={Styles.container}>
            <h3 className={Styles.title}>CSV Datei Import</h3>

            {/* File Upload Section */}
            <div className={Styles.uploadSection}>
                <h4>1. CSV Datei auswählen</h4>
                <div {...getRootProps()} className={Styles.dropzone}>
                    <input {...getInputProps()} />
                    <div className={Styles.uploadContent}>
                        <UploadSvg className={Styles.uploadIcon} />
                        {isDragActive ? (
                            <p>CSV Datei hier ablegen...</p>
                        ) : (
                            <p>
                                <span className={Styles.clickText}>CSV Datei hochladen</span>
                                <span> (oder per Drag&Drop)</span>
                            </p>
                        )}
                    </div>
                </div>

                {selectedFile && (
                    <div className={Styles.selectedFile}>
                        <div className={Styles.fileInfo}>
                            <span className={Styles.fileName}>{selectedFile.name}</span>
                            <span className={Styles.fileSize}>
                                ({(selectedFile.size / 1024).toFixed(1)} KB)
                            </span>
                        </div>
                        <button
                            className={Styles.removeButton}
                            onClick={handleCancel}
                            type="button"
                        >
                            ✕
                        </button>
                    </div>
                )}
            </div>

            {/* Selected Profile Display */}
            <div className={Styles.profileSection}>
                <h4>2. Ausgewähltes Profil</h4>
                <div className={Styles.selectedProfileDisplay}>
                    <p><strong>{selectedProfile?.name}</strong></p>
                    {selectedProfile?.description && (
                        <p className={Styles.profileDescription}>{selectedProfile.description}</p>
                    )}
                </div>
            </div>

            {/* Profile Settings Display */}
            <div className={Styles.profileSettingsSection}>
                <h4>3. Profil-Einstellungen</h4>
                <div className={Styles.profileSettings}>
                    <div className={Styles.settingRow}>
                        <strong>Preisbehandlung:</strong> {selectedProfile?.defaultValues?.withPrices ? 'Mit Preisen' : 'Ohne Preise'}
                    </div>
                    <div className={Styles.settingRow}>
                        <strong>Verkaufskanal:</strong> {
                            selectedProfile?.defaultValues?.salesChannelId 
                                ? (salesChannels.find(sc => sc.id === selectedProfile.defaultValues.salesChannelId)?.name || 
                                   `ID: ${selectedProfile.defaultValues.salesChannelId}`)
                                : 'Nicht konfiguriert'
                        }
                    </div>
                    <div className={Styles.settingRow}>
                        <strong>Versandart:</strong> {
                            selectedProfile?.defaultValues?.shippingMethodId 
                                ? (shippingMethods.find(sm => sm.id === selectedProfile.defaultValues.shippingMethodId)?.name || 
                                   `ID: ${selectedProfile.defaultValues.shippingMethodId}`)
                                : 'Nicht konfiguriert'
                        }
                    </div>
                </div>
            </div>

            {/* Import Button */}
            <div className={Styles.actionSection}>
                <h4>4. Import starten</h4>
                <button
                    className={Styles.importButton}
                    onClick={handleImport}
                    disabled={!selectedFile || !selectedProfile || !selectedProfile?.defaultValues?.salesChannelId || isImporting}
                >
                    {isImporting ? (
                        <>
                            <img src={Loader} width={20} height={20} alt="Loading" />
                            Import läuft...
                        </>
                    ) : (
                        'Import starten'
                    )}
                </button>
            </div>

            {/* Import Results */}
            {importStats && (
                <div className={Styles.resultsSection}>
                    <h4>Import Ergebnisse</h4>
                    
                    {importStats.imported?.length > 0 && (
                        <div className={Styles.successStats}>
                            <h5>✅ Erfolgreich importiert ({importStats.imported.length})</h5>
                            <ul>
                                {importStats.imported.map((orderNumber, index) => (
                                    <li key={index}>
                                        <strong>{orderNumber}</strong>
                                        {importStats.orders?.[index] && (
                                            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                                Order ID: {importStats.orders[index].id}<br/>
                                                Artikel: {importStats.orders[index].lineItemsCount || 'N/A'}<br/>
                                                Status: {importStats.orders[index].status || 'erstellt'}
                                            </div>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {importStats.skipped?.length > 0 && (
                        <div className={Styles.skippedStats}>
                            <h5>⚠️ Übersprungen ({importStats.skipped.length})</h5>
                            <ul>
                                {importStats.skipped.map((orderNumber, index) => (
                                    <li key={index}>{orderNumber}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {importStats.errors?.length > 0 && (
                        <div className={Styles.errorStats}>
                            <h5>❌ Fehler ({importStats.errors.length})</h5>
                            <ul>
                                {importStats.errors.map((error, index) => (
                                    <li key={index}>
                                        <strong>{error.orderNumber || 'Unbekannt'}:</strong> {error.error}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default CSVImporter;